/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table";
import React from "react";
// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 PRO React examples
import DataTableHeadCell from "./DataTableHeadCell";
import DataTableBodyCell from "./DataTableBodyCell";
import {
  Checkbox,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TableCell,
} from "@mui/material";
import MDButton from "components/MDButton";
import { useMaterialUIController } from "context";
import ContactModel from "cruds/campaign/contacts/model";
import AdvancedFilter from "cruds/campaign/advancedFilter";
import { handleShiftKeySelection } from "utils/rowSelection";
import { TableData } from "./TableData";
import { VirtualizedTableData } from "./VirtualizedTableData";
import { height } from "@mui/system";
import GenerateContactModel from "cruds/campaign/contacts/generate";

function CustomizeDataTable({
  entriesPerPage,
  canSearch,
  canRefresh,
  searchFun,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  loader = false,
  deepSearch,
  isSelection,
  isMultiSelect = true,
  selectedRows,
  setSelectedRows,
  tableHeight = "calc(100vh - 220px)",
  fixedWidth = false,
  selectData = [],
  selected,
  selecetdFunc,
  generate,
  columnSetting,
  debouncePageIndex,
  setDebouncedPageIndex,
  pageIndex,
  setPageIndex,
  setApiSearch,
  pageSize,
  setPageSize,
  total,
  onSave,
  sorting,
  onSortChange,
  clearAdvanceFilter,
  setAdvanceFilter,
  selectedAll,
  setSelectedAll,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [initialRowIndex, setInitialRowIndex] = useState(null);
  const [currentRowIndex, setCurrentRowIndex] = useState(null);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenu, setOpenMenu] = useState(false);

  const defaultValue = entriesPerPage.defaultValue || 25;
  const entries = entriesPerPage.entries?.map((el) => el.toString()) || ["5", "10", "15", "20", "25", "50", "100"];
  const entriesStart = pageIndex === 0 ? 1 : pageIndex * pageSize + 1;
  const entriesEnd = (pageIndex + 1) * pageSize < total ? (pageIndex + 1) * pageSize : total;
  const selectedCompany = table.rows.filter((e) => selectedRows.includes(e.company_id))[0]?.CampaignCompany;

  
  const columns = useMemo(() => table.columns.filter((column) => column.show !== false), [table.columns]);
  const data = useMemo(() => table.rows, [table.rows]);

  const tableInstance = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: Math.ceil(total / pageSize),
      initialState: { pageIndex, pageSize },
    },
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    setPageSize: setTablePageSize,
  } = tableInstance;

  useEffect(() => setPageSize(defaultValue), [defaultValue]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPageIndex(debouncePageIndex);
      gotoPage(debouncePageIndex);
    }, 1000);

    return () => clearTimeout(handler);
  }, [debouncePageIndex, gotoPage]);

  useEffect(() => {
    const selectRowHandler = (index) => {
      setSelectedRows((prevSelectedRows) => {
        const rowData = page[index]?.original.id;

        if (prevSelectedRows.includes(rowData)) {
          return prevSelectedRows.filter((row) => row !== rowData);
        } else {
          return [...prevSelectedRows, rowData];
        }
      });
    };

    const handleKeyDown = (event) => {
      handleShiftKeySelection({
        event,
        currentRowIndex,
        initialRowIndex,
        pageLength: page.length,
        selectRowHandler,
        setCurrentRowIndex,
      });
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentRowIndex]);

  useEffect(() => {
    const handler = setTimeout(() => setApiSearch(search), 1000);
    return () => clearTimeout(handler);
  }, [search]);

  const handlePageChange = (newPageIndex) => {
    const validPageIndex = Math.max(0, Math.min(newPageIndex, pageOptions.length - 1));
    setDebouncedPageIndex(validPageIndex);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setTablePageSize(newPageSize);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
  };

  const handleCloseMenu = () => {
    setOpenMenu(false);
  };

  const renderPagination = pageOptions.map((option) => (
    <MDPagination item key={option} onClick={() => handlePageChange(option)} active={pageIndex === option}>
      {option + 1}
    </MDPagination>
  ));

  return (
    <TableContainer
      sx={{
        maxHeight: tableHeight,
        display: "flex",
        flexDirection: "column",
        boxShadow: "none",
      }}
    >
      {/* Header */}
      {(entriesPerPage || canSearch || canRefresh) && (
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pl={3}
          pr={3}
          pt={1}
          pb={1}
          sx={{
            flexShrink: 0,
            backgroundColor: "white",
            position: "sticky",
            flexWrap: "wrap",
            gap: 1,
            top: 0,
            zIndex: 2,
          }}
        >
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  const parsedValue = parseInt(newValue, 10);
                  if (!isNaN(parsedValue)) {
                    handlePageSizeChange(parsedValue);
                  }
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        const parsedValue = parseInt(event.target.value, 10);
                        if (!isNaN(parsedValue)) {
                          handlePageSizeChange(parsedValue);
                        }
                      }
                    }}
                  />
                )}
              />
              <MDTypography
                variant="caption"
                color="secondary"
                sx={{
                  marginLeft: { xs: "2px" },
                  marginRight: { xs: "2px" },
                }}
              >
                <MDTypography sx={{ display: { xs: "none" } }}>&nbsp;&nbsp;</MDTypography>
                entries per page
              </MDTypography>
            </MDBox>
          )}
          <MDBox display="flex" alignItems="center">
            {canRefresh && (
              <MDBox width="4rem" ml="auto" style={{ marginRight: "10px" }}>
                <MDButton size="small" variant="gradient" color="dark" fullWidth onClick={() => (!loader ? searchFun() : {})}>
                  <i className={"fas fa-refresh"}></i>
                </MDButton>
              </MDBox>
            )}

            {/* Dropdown Menu for other buttons */}
            <FormControl sx={{ m: 1, minWidth: 120 }}>
              <MDButton variant="contained" color="info" onClick={handleMenuClick}>
                Actions
              </MDButton>
              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={handleCloseMenu}
              >
                <MenuItem>
                  <AdvancedFilter
                    setFilter={setAdvanceFilter}
                    clearFilter={() => {
                      clearAdvanceFilter();
                      setSelectedAll(false);
                    }}
                    componentName="dashboard"
                    selectedCampaign={localStorage.getItem("selectedCampaign")}
                    handleCloseMenu={handleCloseMenu}
                  />
                </MenuItem>
                <MenuItem>
                  <MDButton variant={"contained"} onClick={() => {columnSetting(), handleCloseMenu()}}>
                    Set Columns
                  </MDButton>
                </MenuItem>
                {selectedRows?.length ? (
                  <MenuItem>
                    <MDButton variant={"contained"} onClick={() => {generate(), handleCloseMenu()}}>
                      Select Jobs
                    </MDButton>
                  </MenuItem>
                ) : null}
                {selectedRows?.length === 1 ? (
                  <MenuItem>
                    <ContactModel company_id={selectedCompany} onSave={onSave} />
                  </MenuItem>
                ) : null}
                {selectedRows?.length === 1 ? (
                  <MenuItem>
                    <GenerateContactModel company_id={selectedCompany} onSave={onSave} />
                  </MenuItem>
                ) : null}
                {selectData?.length ? (
                  <MenuItem>
                    <MDButton variant={"contained"} onClick={() => {selecetdFunc(), handleCloseMenu()}}>
                      Change Campaign
                    </MDButton>
                  </MenuItem>
                ) : null}
              </Menu>
            </FormControl>

            {canSearch && (
              <MDBox width="12rem" ml="auto">
                <MDInput
                  placeholder="Search..."
                  value={search}
                  size="small"
                  fullWidth
                  onChange={({ currentTarget }) => {
                    setSearch(currentTarget.value);
                    // onSearchChange(currentTarget.value);
                  }}
                />
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      )}

      {/* Scrollable Table Data */}
      <MDBox
        sx={{
          flexGrow: 1,
          overflow: "auto",
          height: "100vh"
        }}
        className="table-parent-mdbox"
      >
        {!loader ? (
          // <TableData
          //   tableInstance={tableInstance}
          //   initialRowIndex={initialRowIndex}
          //   isSelection={isSelection}
          //   isMultiSelect={isMultiSelect}
          //   selectedAll={selectedAll}
          //   isSorted={isSorted}
          //   selectedRows={selectedRows}
          //   fixedWidth={fixedWidth}
          //   noEndBorder={noEndBorder}
          //   sorting={sorting}
          //   setSelectedRows={setSelectedRows}
          //   setSelectedAll={setSelectedAll}
          //   setInitialRowIndex={setInitialRowIndex}
          //   setCurrentRowIndex={setCurrentRowIndex}
          //   onSortChange={onSortChange}
          // />


            <VirtualizedTableData
              initialRowIndex={initialRowIndex}
              isSelection={isSelection}
              isMultiSelect={isMultiSelect}
              selectedAll={selectedAll}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              setSelectedAll={setSelectedAll}
              memoizedRows={data}
              memoizedColumns={columns}
              setInitialRowIndex={setInitialRowIndex}
              setCurrentRowIndex={setCurrentRowIndex}
              sorting={sorting}
              onSortChange={onSortChange}
            />
        ) : (
          <StackSkeleton />
        )}
      </MDBox>

      {/* Footer */}
      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        pt={!showTotalEntries && pageOptions.length === 1 ? 0 : 1}
        pb={!showTotalEntries && pageOptions.length === 1 ? 0 : 1}
        pl={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
        pr={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
        sx={{
          flexShrink: 0,
          backgroundColor: "white",
          position: "sticky",
          bottom: 0,
          zIndex: 2,
        }}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {total} entries
            </MDTypography>
          </MDBox>
        )}
        {pageOptions.length > 1 && (
          <MDPagination variant={pagination.variant || "gradient"} color={pagination.color || "info"}>
            {canPreviousPage && (
              <MDPagination item onClick={() => handlePageChange(pageIndex - 1)}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPagination.length > 6 ? (
              <MDBox width="5rem" mx={1}>
                <MDInput
                  inputProps={{
                    type: "number",
                    min: 1,
                    max: pageOptions.length,
                  }}
                  value={Number(debouncePageIndex) + 1}
                  onChange={(e) => handlePageChange(Number(e.target.value) - 1)}
                />
              </MDBox>
            ) : (
              renderPagination
            )}
            {canNextPage && (
              <MDPagination item onClick={() => handlePageChange(pageIndex + 1)}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  );
}

// Setting default values for the props of CustomizeDataTable
CustomizeDataTable.defaultProps = {
  entriesPerPage: { defaultValue: 25, entries: [5, 10, 15, 20, 25, 50] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
  deepSearch: false,
  fixedWidth: false,
};

// Typechecking props for the CustomizeDataTable
CustomizeDataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canRefresh: PropTypes.bool,
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  searchFun: PropTypes.func,
  generate: PropTypes.func,
  selected: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selecetdFunc: PropTypes.func,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark", "light"]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  deepSearch: PropTypes.bool,
  fixedWidth: PropTypes.bool,
  company_id: PropTypes.number,
  onSave: PropTypes.func,
};

function StackSkeleton() {
  return (
    <Stack spacing={0.5}>
      <Skeleton variant="rectangular" sx={{ fontSize: "4rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
    </Stack>
  );
}

export default React.memo(CustomizeDataTable);
